import { Field, Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { TEXTAREA_ROW_LENGTH } from '../../../../constants/constants';
import { AccountStatusEnum } from '../../../../constants/enums';
import userTypeEnum from '../../../../constants/enums/user-type-enum';
import { AccountStatusTypeOptions } from '../../../../constants/options.constant';
import validAllowedCharacters from '../../../../constants/valid-character-password';
import {
  getTenantIdFromLocalStorage,
  getUserType,
} from '../../../../helpers/localstorage-helper';
import { IAddEditFormProps } from '../../../../models/forms/IForm';
import { IMultiSelectOption, IOption } from '../../../../models/ICommon';
import LoaderButton from '../../../../shared-components/button/loader-button';
import { MultiSelectWithChips } from '../../../../shared-components/FormikFormHandlers/FormikMultiSelectWithChips';
import FormikFormInput from '../../../../shared-components/FormikFormHandlers/FormikFormInput';
import FormikFormSelect from '../../../../shared-components/FormikFormHandlers/FormikFormSelect';
import FormikFormTextArea from '../../../../shared-components/FormikFormHandlers/FormikFormTextArea';
import {
  AddUserValidateSchema,
  EditUserValidateSchema,
} from '../../../../shared-components/FormikFormHandlers/FormikValidationSchema';
import FormLoader from '../../../../shared-components/FormLoader/FormLoader';
import Label from '../../../../shared-components/label/label';
import { formatMessage } from '../../../../translations/format-message';
import './add-edit-base-user-form.css';

interface IProps extends IAddEditFormProps<any> {
  userTypeOptions: IOption[];
  tenantOptions: IOption[];
  addUpdateUserLoading: boolean;
  isUserTypeSuperAdminOrDIDManager: boolean;
  isReserved: boolean;
  tenantBlankOptions: IOption[];
  allProcessOptionsNew: IMultiSelectOption[];
  allUserAssignedMulitSelectOptions: IMultiSelectOption[];
}

const AddEditBaseUserForm = (props: IProps) => {
  const [showHidePassword, setShowHidePassword] = useState(false);
  const [showHideConfirmPassword, setShowHideConfirmPassword] = useState(false);
  const [toggleFactoryField, setToggleFactoryField] = useState(false);
  const [toggleEmailRequired, setToggleEmailRequired] = useState(false);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (props.allUserAssignedMulitSelectOptions) {
      setSelected(props.allUserAssignedMulitSelectOptions);
    }
  }, [props.allUserAssignedMulitSelectOptions]);

  return (
    <>
      <Modal
        className=""
        size="lg"
        show={true}
        centered={true}
        onHide={() => {}}
      >
        <Modal.Header>
          <Modal.Title>
            {props.id === -1 ? (
              getUserType() === 'SuperAdministrator' ? (
                <FormattedMessage id="add_system_user" />
              ) : (
                <FormattedMessage id="add_system_user" />
              )
            ) : (
              <FormattedMessage id="update_system_user" />
            )}
            <button
              onClick={props.onModalClosed}
              disabled={props.addUpdateUserLoading}
              className="Crossicons"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="23"
                height="23"
                fill="currentColor"
                className="bi bi-x"
                viewBox="0 0 16 16"
              >
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
              </svg>
            </button>
          </Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={
            getTenantIdFromLocalStorage() === '-1'
              ? props.initialValues
              : {
                  ...props.initialValues,
                  TenantId: props.tenantOptions[1]?.id || -1,
                }
          }
          enableReinitialize
          validationSchema={
            props.id === -1 ? AddUserValidateSchema : EditUserValidateSchema
          }
          validateOnBlur={false}
          onSubmit={async (values) => {
            props.submitHandler({
              ...values,
              ProcessId: selected.map((item) => item.value).join(','),
            });
          }}
        >
          {({ dirty, setFieldValue, handleBlur, handleChange, values }) => {
            useEffect(() => {
              const isFactorySelected =
                values.TenantId !== undefined &&
                values.TenantId !== null &&
                values.TenantId !== '' &&
                values.TenantId !== -1 &&
                values.TenantId !== '-1';

              if (
                isFactorySelected &&
                (Number(values.UserType) === 0 || Number(values.UserType) === 1)
              ) {
                setFieldValue('UserType', -1);
              }
            }, [values.TenantId, values.UserType, setFieldValue]);

            return (
              <Form className="user-form">
                {props.isLoading ? (
                  <FormLoader loading={props.isLoading} contained />
                ) : null}
                <Modal.Body>
                  <div className="border-box">
                    <div className="row">
                      <h6 className="border-box-heading">
                        {formatMessage('user_detail_header')}
                      </h6>
                      <div className="col-md-6 col-12 email-active">
                        <div className="form-group">
                          <Label
                            className="label-style"
                            required={toggleEmailRequired}
                            label="email"
                          />

                          <div className="input-group">
                            <Field
                              data-testid="testEmail"
                              className="form-control"
                              name="Email"
                              type="text"
                              disabled={props.id > 0 && !toggleEmailRequired}
                              invalidcharacter={[' ']}
                              validcharacter={[
                                '-',
                                '_',
                                '[',
                                ']',
                                '(',
                                ')',
                                ':',
                                '#',
                                '@',
                                '.',
                              ]}
                              placeholder=""
                              as={FormikFormInput}
                              aria-describedby="basic-addon2"
                            />
                            <div
                              className={`input-group-append ${props.id > 0 ? 'cursor-not-allowed' : ''}`}
                            >
                              <span
                                className={`input-group-text ${props.id > 0 ? 'pointer-events-none' : ''}`}
                                id="basic-addon2"
                              >
                                <Field
                                  className={`form-control ${props.id > 0 ? 'pointer-events-none' : ''}`}
                                  name="AccountStatus"
                                  as={FormikFormSelect}
                                  options={AccountStatusTypeOptions}
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    const selectedValue = parseInt(
                                      event.target.value
                                    );
                                    setFieldValue(
                                      'AccountStatus',
                                      selectedValue
                                    );
                                    if (!isNaN(selectedValue)) {
                                      setToggleEmailRequired(
                                        selectedValue ===
                                          AccountStatusEnum.Pending
                                      );
                                    }
                                  }}
                                  disabled
                                />
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6 col-12">
                        <Label
                          className="label-style"
                          required={true}
                          label="display_name"
                        />
                        <Field
                          data-testid="Username"
                          className="form-control"
                          name="UserName"
                          type="text"
                          validcharacter={validAllowedCharacters}
                          placeholder=""
                          as={FormikFormInput}
                        />
                      </div>

                      <div className="col-md-6 col-12">
                        <div className="form-group">
                          <Label
                            className="label-style"
                            required={true}
                            label="user_type"
                          />
                          <Field
                            name="UserType"
                            as={FormikFormSelect}
                            options={
                              (values.TenantId !== undefined &&
                              values.TenantId !== null &&
                              values.TenantId !== '' &&
                              values.TenantId !== -1 &&
                              values.TenantId !== '-1')
                                ? props.userTypeOptions.filter(
                                    (option) => option.id !== 0 && option.id !== 1
                                  )
                                : props.userTypeOptions
                            }
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              const selectedValue = parseInt(
                                event.target.value
                              );
                              setFieldValue('UserType', selectedValue);
                              if (!isNaN(selectedValue)) {
                                if (
                                  selectedValue ===
                                    userTypeEnum.SuperAdministrator ||
                                  selectedValue === userTypeEnum.DIDManager
                                ) {
                                  setToggleFactoryField(true);
                                } else {
                                  setToggleFactoryField(false);
                                }
                              }
                            }}
                            disabled={props.isReserved}
                            className="form-control"
                          />
                        </div>
                      </div>
                      {
                        <div className="col-md-6 col-12">
                          <div className="form-group">
                            <Label
                              className="label-style "
                              required={!toggleFactoryField}
                              label="factory"
                            />
                            <Field
                              name="TenantId"
                              as={FormikFormSelect}
                              disabled={
                                toggleFactoryField ||
                                props.id > 0 ||
                                getTenantIdFromLocalStorage() !== '-1'
                              }
                              options={
                                toggleFactoryField
                                  ? props.tenantBlankOptions
                                  : props.tenantOptions
                              }
                              setDefaultValue={true}
                              className="form-control"
                            />
                          </div>
                        </div>
                      }
                      {values.UserType === userTypeEnum.Operator && (
                        <div className="col-md-12 col-12">
                          <div className="form-group">
                            <Label
                              className="label-style"
                              required={false}
                              label="process"
                            />

                            <MultiSelectWithChips
                              options={props.allProcessOptionsNew}
                              value={selected}
                              onChange={setSelected}
                              placeholder="select_process"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {props.id < 0 && (
                    <div className=" mt-4 border-box mb-2">
                      <div className="row">
                        <h6 className="border-box-heading">
                          {formatMessage('login_detail_header')}
                        </h6>
                        {props.id < 0 && (
                          <div className="col-md-4 col-12">
                            <div className="form-group formSet">
                              <Label
                                className="label-style"
                                required={true}
                                label="user_id"
                              />
                              <Field
                                data-testid="UserId"
                                className="form-control"
                                name="UserId"
                                placeholder=""
                                type="text"
                                validcharacter={validAllowedCharacters}
                                as={FormikFormInput}
                                onBlur={handleBlur}
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                        )}
                        {props.id < 0 && (
                          <>
                            <div className="col-md-4 col-12 password-icon">
                              <div className="form-group">
                                <Label
                                  className="label-style"
                                  required={true}
                                  label="user_password"
                                />
                                <div>
                                  <Field
                                    data-testid="testPassword"
                                    className="form-control"
                                    name="Password"
                                    autoComplete="new-password"
                                    type={
                                      showHidePassword ? 'text' : 'password'
                                    }
                                    invalidcharacter={[' ']}
                                    validcharacter={validAllowedCharacters}
                                    placeholder=""
                                    as={FormikFormInput}
                                  />
                                  {showHidePassword ? (
                                    <svg
                                      type="button"
                                      onClick={() => {
                                        setShowHidePassword(!showHidePassword);
                                      }}
                                      viewBox="0 0 24 24"
                                      width="20"
                                      height="20"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      fill="none"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      className="showHidePass css-i6dzq1 modalBox"
                                    >
                                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                      <circle
                                        cx="12"
                                        cy="12"
                                        r="3"
                                      ></circle>
                                    </svg>
                                  ) : (
                                    <svg
                                      type="button"
                                      onClick={() => {
                                        setShowHidePassword(!showHidePassword);
                                      }}
                                      viewBox="0 0 24 24"
                                      width="20"
                                      height="20"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      fill="none"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      className="showHidePass css-i6dzq1 modalBox"
                                    >
                                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                      <line
                                        x1="1"
                                        y1="1"
                                        x2="23"
                                        y2="23"
                                      ></line>
                                    </svg>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="col-md-4 col-12 password-icon">
                              <div className="form-group">
                                <Label
                                  className="label-style"
                                  required={true}
                                  label="confirm_password"
                                />
                                <div>
                                  <Field
                                    data-testid="testConfirmPassword"
                                    className="form-control"
                                    name="ConfirmPassword"
                                    autoComplete="confirm-new-password"
                                    type={
                                      showHideConfirmPassword
                                        ? 'text'
                                        : 'password'
                                    }
                                    invalidcharacter={[' ']}
                                    validcharacter={validAllowedCharacters}
                                    placeholder=""
                                    as={FormikFormInput}
                                  />
                                  {showHideConfirmPassword ? (
                                    <svg
                                      type="button"
                                      onClick={() => {
                                        setShowHideConfirmPassword(
                                          !showHideConfirmPassword
                                        );
                                      }}
                                      viewBox="0 0 24 24"
                                      width="20"
                                      height="20"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      fill="none"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      className="showHidePass css-i6dzq1 modalBox"
                                    >
                                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                      <circle
                                        cx="12"
                                        cy="12"
                                        r="3"
                                      ></circle>
                                    </svg>
                                  ) : (
                                    <svg
                                      type="button"
                                      onClick={() => {
                                        setShowHideConfirmPassword(
                                          !showHideConfirmPassword
                                        );
                                      }}
                                      viewBox="0 0 24 24"
                                      width="20"
                                      height="20"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      fill="none"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      className="showHidePass css-i6dzq1 modalBox"
                                    >
                                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                      <line
                                        x1="1"
                                        y1="1"
                                        x2="23"
                                        y2="23"
                                      ></line>
                                    </svg>
                                  )}
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="row">
                    <div className="col-12">
                      <div className="form-group formSet mt-3">
                        <Label
                          className="label-style"
                          required={false}
                          label="Comment"
                        />
                        <Field
                          rows={TEXTAREA_ROW_LENGTH}
                          id="Comment"
                          className="form-control"
                          name="Comment"
                          placeholder=""
                          type="text"
                          as={FormikFormTextArea}
                        />
                      </div>
                    </div>
                  </div>
                </Modal.Body>
                <Modal.Footer>
                  <div className="d-flex justify-content-center ">
                    <div className="row tab-action g-0">
                      <div className="col-12">
                        <Button
                          variant="light-danger"
                          type="button"
                          onClick={props.onModalClosed}
                          disabled={props.addUpdateUserLoading}
                        >
                          <FormattedMessage id="cancel" />
                        </Button>
                        <LoaderButton
                          type="submit"
                          isLoading={props.addUpdateUserLoading}
                          text="button_save"
                        />
                      </div>
                    </div>
                  </div>
                </Modal.Footer>
              </Form>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
};

export default AddEditBaseUserForm;
